import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { RichText, toPlainText } from './chatConfig';
import { getSearchHistory } from './chatHistoryMock';
import ChatIcon from './ChatIcon';
import chevronIcon from '../../styles/assets/img/chat/chevron.svg';
import termsIcon from '../../styles/assets/img/chat/nav-terms.svg';
import tipsIcon from '../../styles/assets/img/chat/nav-tips.svg';
import historyIcon from '../../styles/assets/img/chat/nav-history.svg';
import newChatIcon from '../../styles/assets/img/chat/nav-new-chat.svg';
import homeIcon from '../../styles/assets/img/chat/nav-home.svg';
import historyHeaderIcon from '../../styles/assets/img/chat/history-header.svg';
import tipsHeaderIcon from '../../styles/assets/img/chat/tips-header.svg';
import tipsSuggestedIcon from '../../styles/assets/img/chat/tips-suggested.svg';

/** Views reachable from the chat menu (besides the conversation itself). */
export const CHAT_VIEWS = {
    menu: 'menu',
    terms: 'terms',
    tips: 'tips',
    history: 'history',
    suggested: 'suggested',
};

function Chevron({ direction = 'right' }) {
    return (
        <span className={`chat-chevron chat-chevron-${direction}`} aria-hidden="true">
            <ChatIcon src={chevronIcon} className="chat-chevron-icon" />
        </span>
    );
}

/** Rounded, outlined row with a trailing chevron (history items, "Try Suggested Searches"). */
function ChatCallout({ icon, children, onClick }) {
    return (
        <button type="button" className="chat-callout" onClick={onClick}>
            <span className="chat-callout-text">
                {icon && <ChatIcon src={icon} className="chat-callout-icon" />}
                <span>{children}</span>
            </span>
            <Chevron />
        </button>
    );
}

/** Shared layout for the menu sub-screens: title + icon, content, Back link. */
function ChatScreen({ title, icon, onBack, children }) {
    return (
        <div className="chat-screen">
            <div className="chat-screen-content">
                <h2 className="chat-screen-title">
                    {title}
                    {icon && <ChatIcon src={icon} className="chat-screen-title-icon" />}
                </h2>
                {children}
            </div>
            <button type="button" className="chat-screen-back" onClick={onBack}>
                <Chevron direction="left" />
                <span>Back</span>
            </button>
        </div>
    );
}

/** The menu opened from the header's ☰ icon. */
export function ChatNavMenu({ onNavigate, onNewChat, onHome }) {
    const items = [
        { label: 'Terms and Privacy Policies', icon: termsIcon, iconClass: 'chat-nav-icon-terms', onClick: () => onNavigate(CHAT_VIEWS.terms) },
        { label: 'Search Tips', icon: tipsIcon, onClick: () => onNavigate(CHAT_VIEWS.tips) },
        { label: 'Search History', icon: historyIcon, onClick: () => onNavigate(CHAT_VIEWS.history) },
        { label: 'New Chat', icon: newChatIcon, onClick: onNewChat },
        { label: 'Home', icon: homeIcon, onClick: onHome },
    ];

    return (
        <nav className="chat-nav" aria-label="Chat menu">
            {items.map((item) => (
                <button key={item.label} type="button" className="chat-nav-item" onClick={item.onClick}>
                    <span className="chat-nav-item-label">
                        <ChatIcon src={item.icon} className={`chat-nav-icon ${item.iconClass || ''}`} />
                        {item.label}
                    </span>
                    <Chevron />
                </button>
            ))}
        </nav>
    );
}

export function ChatSearchTips({ onBack, onTrySuggested }) {
    return (
        <ChatScreen title="Search Tips" icon={tipsHeaderIcon} onBack={onBack}>
            <div className="chat-screen-text">
                <p>In search of inspiration? Look no further. There's a lot of information you can use to prep for conversations, but we've collected the core essentials here.</p>
                <p>After you've picked a market, product, and territory, we can pull lots of information to compare and speed up prep time, including:</p>
                <ul>
                    <li>Info about a specific HCP, like their top payers, or their key insights (decile, channel mix, product preferences, and recent wins)</li>
                    <li>Info about a specific payer, like their top HCPs or coverage status</li>
                    <li>Info about your selected region, like a list of new writers, or recent wins (and HCPs to target)</li>
                </ul>
                <p>The best way is to learn by doing. We have some premade questions you can use to explore your product, market, and territory data. Tap below to get started:</p>
            </div>
            <ChatCallout icon={tipsSuggestedIcon} onClick={onTrySuggested}>
                Try Suggested Searches
            </ChatCallout>
        </ChatScreen>
    );
}

export function ChatSuggestedSearches({ suggestions, onBack, onAsk }) {
    return (
        <ChatScreen title="Suggested Searches" icon={tipsHeaderIcon} onBack={onBack}>
            <p className="chat-screen-subtitle">Select a question below to get started:</p>
            <div className="chat-callout-list">
                {suggestions.map((s) => (
                    <ChatCallout key={s} onClick={() => onAsk(toPlainText(s))}>
                        <RichText text={s} />
                    </ChatCallout>
                ))}
            </div>
        </ChatScreen>
    );
}

export function ChatSearchHistory({ onBack, onAsk }) {
    const [history, setHistory] = useState(null);

    useEffect(() => {
        let active = true;
        getSearchHistory().then((items) => {
            if (active) setHistory(items);
        });
        return () => { active = false; };
    }, []);

    return (
        <ChatScreen title="Search History" icon={historyHeaderIcon} onBack={onBack}>
            <p className="chat-screen-subtitle">Showing the last 7 days of searches:</p>
            {history === null && <p className="chat-screen-subtitle">Loading…</p>}
            {history?.length === 0 && <p className="chat-screen-subtitle">No searches yet.</p>}
            <div className="chat-history-list">
                {history?.map((item) => (
                    <div key={item.id} className="chat-history-item">
                        <ChatCallout onClick={() => onAsk(item.question)}>{item.question}</ChatCallout>
                        <span className="chat-history-date">{dayjs(item.searchedAt).format('MM/DD/YYYY hh:mm A')}</span>
                    </div>
                ))}
            </div>
        </ChatScreen>
    );
}

export function ChatTerms({ content, onBack }) {
    const html = useMemo(
        () => (content ? DOMPurify.sanitize(marked.parse(content)) : ''),
        [content]
    );

    return (
        <ChatScreen title="Terms and Privacy Policies" onBack={onBack}>
            {html ? (
                <div className="chat-screen-text chat-markdown" dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
                <p className="chat-screen-subtitle">Terms and privacy policies have not been configured yet.</p>
            )}
        </ChatScreen>
    );
}
